<script lang="ts">
  import {slide, fly} from 'svelte/transition';
  import InlineSVG from 'svelte-inline-svg';

  import {navigateTo, router} from 'yrv';

  import type {DateRange} from '../../types/date';
  import {
    Confidentiality,
    Visibility,
    DiamondSector,
    Topic,
    Service,
    Task,
    Field,
    Technology,
    Format,
    type ImageFile,
    type ImageMedia,
    type VideoMedia,
    type ProjectURL,
    type ProjectFormData,
    type ProjectDraftFormData,
    type DevelopmentProject,
    type ConsultingProject,
    type Project,
    type ProjectDraft,
    ProjectType
  } from '../../types/project';

  import {
    youtubeVideoUrlRegex,
    driveVideoUrlRegex,
    helpUrl,
    diamondHelpUrl
  } from '../../constants/urls';

  import {
    customers as availableCustomers,
    projectLead as availableProjectLeads,
    teamAndProjectLeads as availableTeamAndProjectLeads,
    fields as availableProjectFields,
    topics as availableProjectTopics,
    services as availableProjectServices,
    tasks as availableProjectTasks,
    technologies as availableProjectTechnologies,
    methods as availableProjectMethods,
    projects
  } from '../../stores/projects';
  import {auth} from '../../stores/user';

  import splitAndTrim from '../../utils/split-and-trim';
  import isProjectDraft from '../../utils/project-draft';

  import helpIcon from '../../assets/icons/help.svg';
  import closeIcon from '../../assets/icons/cross.svg';
  import infoIcon from '../../assets/icons/info.svg';

  import RadiosSelect from './RadiosSelect.svelte';
  import TextInput from './TextInput.svelte';
  import Subheading from './ProjectFormSubheading.svelte';
  import MultipleUrlInputs from './MultipleUrlInputs.svelte';
  import MultipleTextInputs from './MultipleTextInputs.svelte';
  import TextAreaInput from './TextAreaInput.svelte';
  import FilesInput from './FilesInput.svelte';
  import TagsInput from './TagsInput.svelte';
  import ImageCaptionsInput from './ImageCaptionsInput.svelte';
  import NumberInput from './NumberInput.svelte';
  import DateRangePicker from '../date-range-picker/DateRangePicker.svelte';
  import IconButton from '../icon-button/IconButton.svelte';
  import Button from '../button/Button.svelte';
  import RangeInput from '../range-input/RangeInput.svelte';
  import Diamond from '../diamond/Diamond.svelte';
  import Note from '../note/Note.svelte';

  const UBILABS_INCORPORATION_DATE = new Date('2007-03-22');

  export let onSubmit: (
    projectData: ProjectFormData | ProjectDraftFormData,
    files: Array<ImageFile>,
    captions: Record<string, string>
  ) => Promise<void>;
  export let initialValues: Project | ProjectDraft | null = null;
  export let projectType: ProjectType;

  let showHelp = true;
  let isSaving = false;
  let formElement: HTMLFormElement;
  let titleInputElement: TextInput;

  let confidentiality = initialValues?.confidentiality || '';
  let visibility = initialValues?.visibility || '';
  let diamond = initialValues?.diamond || {
    [DiamondSector.WEB]: 0,
    [DiamondSector.LOCATION]: 0,
    [DiamondSector.CLOUD]: 0,
    [DiamondSector.DATA]: 0
  };
  let title = initialValues?.title || '';
  let title_En = initialValues?.title_En || '';
  let description = initialValues?.description || '';
  let descriptionEng = initialValues?.description_En || '';
  let customers = initialValues?.customers?.join(',') || '';
  let contactName = initialValues?.contactPerson?.name || '';
  let contactUrl = initialValues?.contactPerson?.url || '';
  let projectLead = initialValues?.projectLead?.join(',') || '';
  let team = initialValues?.team?.join(',') || '';
  let fields = initialValues?.fields?.join(',') || '';
  let topics = initialValues?.topics?.join(',') || '';
  let services = initialValues?.services?.join(',') || '';
  let tasks = (initialValues as DevelopmentProject)?.tasks?.join(',') || '';
  let technologies =
    (initialValues as DevelopmentProject)?.technologies?.join(',') || '';
  let valueInTime = (initialValues as ConsultingProject)?.valueInTime || null;
  let valueInPrice = (initialValues as ConsultingProject)?.valueInPrice || null;
  let zohoUrl = (initialValues as ConsultingProject)?.zohoUrl || '';
  let formats = (initialValues as ConsultingProject)?.formats?.join(',') || '';
  let methods = (initialValues as ConsultingProject)?.methods?.join(',') || '';
  let creationPeriod: DateRange = initialValues?.creationPeriod || {
    start: null,
    end: null
  };
  let urls = initialValues?.urls?.length
    ? initialValues?.urls
    : [{type: '', url: ''}];
  let media = initialValues?.media || [];
  let files: Array<ImageFile> = [];
  let images = media.filter<ImageMedia>(
    (mediaEntry): mediaEntry is ImageMedia => mediaEntry.type === 'image'
  );
  let captions: Record<string, string> = images.reduce(
    (captionsByName, {name, caption}) => ({
      ...captionsByName,
      [name]: caption || ''
    }),
    {}
  );
  const videoMedia = media.filter<VideoMedia>(
    (mediaEntry): mediaEntry is VideoMedia => mediaEntry.type === 'video'
  );
  let videos = videoMedia.length ? videoMedia.map(({url}) => url) : [null];
  let relatedProjects = initialValues?.relatedProjects?.join(',') || '';

  const titlePlaceholder = 'Unser Projekt';
  const titlePlaceholderEnglish = 'Our project';
  const descriptionPlaceholder =
    'Das ist unser Projekt und so haben wir es gemacht';
  const descriptionPlaceholderEnglish = 'This is what we did and how we did it';
  const customersPlaceholder = 'Ubilabs GmbH, Ubilabs Inc.';
  const contactNamePlaceholder = 'John Doe';
  const contactUrlPlaceholder = 'Hubspot Contact URL';
  const teamPlaceholder = 'Darius Designer, Diana Developer';
  const projectLeadPlaceholder = 'Lara Lead, Oliver Owner';
  const tasksPlaceholder = 'Feature Demo, Locator, Visualization';
  const technologiesPlaceholder = 'Data Studio, QGIS, React, WebGL';
  const servicesPlaceholder = 'Data Analysis, Development, Reselling / License';
  const methodsPlaceholder = 'Type in and add to suggest list';
  const fieldsPlaceholder = 'Education, Logistics, Travel';
  const topicsPlaceholder = 'Smart City, Sustainability, Technology';
  const urlTypePlaceholder = 'Select a type';
  const urlPlaceholder = 'https://...';
  const videosPlaceholder = 'YouTube or Google Drive URL';

  const diamondTitles = {
    [DiamondSector.WEB]:
      'Means especially the scope and complexity of a front-end solution.',
    [DiamondSector.LOCATION]:
      'Refers to everything we do with a clear reference to location.',
    [DiamondSector.CLOUD]:
      'Primarily means the use of cloud products in the productive operation of the project.',
    [DiamondSector.DATA]:
      'Primarily means the handling of data volumes in databases, streaming pipelines, etc.'
  };

  const onFormChange = () => {
    if (!$router.query.changed) {
      const {path, params, query} = $router;
      navigateTo(path, {
        replace: false,
        reload: true,
        params,
        // Add query parameter to indicate form change
        queryParams: {...query, changed: true}
      });
    }
  };

  /**
   * @param translation - the translation product (translated description)
   * @param baseLanguage - the language from which the translation was made
   */
  const handleTranslation = (
    translation: string,
    baseLanguage: 'de' | 'en'
  ) => {
    if (baseLanguage === 'de') {
      descriptionEng = translation;
    } else if (baseLanguage === 'en') {
      description = translation;
    }
  };

  $: confidentialityOptions = Object.values(Confidentiality).map(
    confidentiality => ({
      value: confidentiality,
      label: confidentiality
    })
  );
  $: visibilityOptions = Object.values(Visibility).map(visibility => ({
    value: visibility,
    label: visibility
  }));

  // Combine predefined values with project values and sort alphabetically (case insensitive)
  $: availableTopics = [
    ...new Set([...Object.values(Topic), ...$availableProjectTopics])
  ].sort(Intl.Collator(undefined, {sensitivity: 'base'}).compare);
  $: availableServices = [
    ...new Set([...Object.values(Service), ...$availableProjectServices])
  ].sort(Intl.Collator(undefined, {sensitivity: 'base'}).compare);
  $: availableTasks = [
    ...new Set([...Object.values(Task), ...$availableProjectTasks])
  ].sort(Intl.Collator(undefined, {sensitivity: 'base'}).compare);
  $: availableFields = [
    ...new Set([...Object.values(Field), ...$availableProjectFields])
  ].sort(Intl.Collator(undefined, {sensitivity: 'base'}).compare);
  $: availableTechnologies = [
    ...new Set([...Object.values(Technology), ...$availableProjectTechnologies])
  ].sort(Intl.Collator(undefined, {sensitivity: 'base'}).compare);

  let projectData: Partial<ProjectFormData | ProjectDraftFormData>;
  $: projectData = (() => {
    const customersArr = splitAndTrim(customers);
    const contactPerson = (contactName.trim().length ||
      contactUrl.trim().length) && {
      ...(contactName.trim() && {name: contactName.trim()}),
      ...(contactUrl.trim() && {url: contactUrl.trim()})
    };
    const projectLeadArr = splitAndTrim(projectLead);
    const teamArr = splitAndTrim(team);
    const tasksArr = splitAndTrim(tasks);
    const technologiesArr = splitAndTrim(technologies);
    const fieldsArr = splitAndTrim(fields);
    const topicsArr = splitAndTrim(topics);
    const formatsArr = splitAndTrim(formats);
    const methodsArr = splitAndTrim(methods);
    const servicesArr = splitAndTrim(services);
    const urlsArr = urls
      .map(({type, url}) => ({type, url: url.trim()}))
      .filter<ProjectURL>((urlObject): urlObject is ProjectURL =>
        Boolean(urlObject.type && urlObject.url)
      );
    const videoArr: Array<VideoMedia> = videos
      .map(videoUrl => ({
        type: 'video' as VideoMedia['type'],
        url: (videoUrl || '').trim()
      }))
      .filter(({url}) => Boolean(url));
    const relatedProjectsArr = splitAndTrim(relatedProjects);

    return {
      ...(confidentiality && {
        confidentiality: confidentiality as Confidentiality
      }),
      ...(visibility && {visibility: visibility as Visibility}),
      ...(Object.values(diamond).some(Boolean) && {diamond}),
      ...((creationPeriod.start || creationPeriod.end) && {creationPeriod}),
      title: title.trim(),
      ...(title_En.trim() && {title_En: title_En.trim()}),
      ...(description.trim() && {description: description.trim()}),
      ...(descriptionEng.trim() && {description_En: descriptionEng.trim()}),
      ...(customersArr.length && {customers: customersArr}),
      ...(contactPerson && {contactPerson}),
      ...(projectLeadArr.length && {projectLead: projectLeadArr}),
      ...(teamArr.length && {team: teamArr}),
      ...(fieldsArr.length && {fields: fieldsArr}),
      ...(topicsArr.length && {topics: topicsArr}),
      ...(servicesArr.length && {services: servicesArr}),
      ...(urlsArr.length && {urls: urlsArr}),
      ...((images.length || videoArr.length) && {
        media: [...images, ...videoArr]
      }),
      ...(projectType === ProjectType.DEVELOPMENT && {
        ...(tasksArr.length && {tasks: tasksArr}),
        ...(technologiesArr.length && {technologies: technologiesArr})
      }),
      ...(projectType === ProjectType.CONSULTING && {
        ...(valueInTime && {valueInTime}),
        ...(valueInPrice && {valueInPrice}),
        ...(zohoUrl && {zohoUrl}),
        ...(formatsArr.length && {formats: formatsArr}),
        ...(methodsArr.length && {methods: methodsArr})
      }),
      ...(relatedProjectsArr.length && {relatedProjects: relatedProjectsArr})
    };
  })();

  $: isDraft = isProjectDraft(projectData, projectType);

  async function save(event: MouseEvent) {
    const formIsValid = isDraft
      ? titleInputElement.reportValidity()
      : formElement.reportValidity();

    if (formIsValid) {
      event.preventDefault();
      isSaving = true;
      await onSubmit(
        projectData as ProjectFormData | ProjectDraftFormData,
        files,
        captions
      );
      isSaving = false;
    }
  }

  async function hasDriveFileOnlyUserPermissions(fileId: string) {
    const {result} = await gapi.client.drive.permissions.list({
      fileId
    });
    return Boolean(result.permissions?.every(({type}) => type === 'user'));
  }

  async function isGoogleDriveVideoAccessible(url: string) {
    const matchDrive = url.match(driveVideoUrlRegex);
    const fileId = matchDrive?.groups?.videoId;

    if (!fileId) {
      throw new Error('fileId was not found.');
    }

    if (!gapi.client.getToken()) {
      try {
        await auth.requestAccessToken();
        return !(await hasDriveFileOnlyUserPermissions(fileId));
      } catch (error) {
        throw error;
      }
    }
    return !(await hasDriveFileOnlyUserPermissions(fileId));
  }
</script>

<style lang="postcss">
  .help > div {
    margin-bottom: 1rem;
    background-color: var(--ubilabs-white);
  }

  @media (min-width: 768px) {
    .help {
      position: absolute;
      top: 0;
      right: 0;
    }

    .help > div {
      z-index: 10;
      position: fixed;
      right: 0;
      margin-right: 1.5rem;
    }
  }

  .help > div > :global(.helpNote) {
    padding-right: 2.5rem;
  }

  @media (min-width: 768px) {
    :global(.helpNote) {
      box-shadow: 0 1px 2px rgba(var(--ubilabs-black-rgb), 0.14),
        0 1px 2px rgba(var(--ubilabs-black-rgb), 0.12);
    }
  }

  :global(.helpNote p) {
    margin: 0;
  }

  :global(.helpNote > div > button) {
    position: absolute;
    top: 0.125rem;
    right: 0.125rem;
    color: inherit;
  }

  form {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 8rem;
  }

  @media (min-width: 768px) {
    .formNote {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      gap: 1.5rem;
      margin: -1rem 0 1rem;
    }

    .formNote > :global(div) {
      grid-column: 2;
    }
  }

  :global(.contactUrl) {
    margin-top: -1rem;
  }

  @media (min-width: 768px) {
    :global(.contactUrl) {
      margin-top: -0.75rem;
    }
  }

  .diamondWrapper {
    display: grid;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 768px) {
    .diamondWrapper {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1.5rem;
    }
  }

  .diamondWrapper > div:first-child {
    display: flex;
    align-items: center;
  }

  @media (min-width: 768px) {
    .diamondWrapper > div:first-child {
      flex-direction: column;
      align-items: end;
    }
  }

  .diamond {
    width: 1.5rem;
    height: 1.5rem;
    margin: 0.25rem 0 0.25rem auto;
  }

  @media (min-width: 768px) {
    .diamond {
      width: 4rem;
      height: 4rem;
    }
  }

  @media (min-width: 768px) {
    form .footer {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      gap: 1.5rem;
    }

    form .footer > div {
      grid-column: 2;
      display: flex;
      justify-content: end;
      column-gap: 0.5rem;
    }
  }

  @media (max-width: 767px) {
    form .footer :global(button) {
      width: 100%;
      justify-content: center;
    }
  }
</style>

{#if showHelp}
  <div class="help">
    <div transition:fly|local={{x: 80, duration: 300}}>
      <Note class="helpNote">
        <span slot="icon"><InlineSVG src={helpIcon} /></span>
        <div>
          <p>Need help?</p>
          <p>Read our <a href={helpUrl} target="_blank">help document</a>.</p>
        </div>
        <IconButton on:click={() => (showHelp = false)}>
          <InlineSVG src={closeIcon} />
        </IconButton>
      </Note>
    </div>
  </div>
{/if}

<form bind:this={formElement} on:change={onFormChange}>
  <Subheading subheading="Privacy" />
  <RadiosSelect
    required
    label="Confidentiality"
    bind:value={confidentiality}
    options={confidentialityOptions}
    id="Confidentiality"
    onChange={event => {
      if (event.currentTarget.value === Confidentiality.CONFIDENTIAL) {
        visibility = Visibility.PRIVATE;
      }
    }} />
  <RadiosSelect
    required
    label="Visibility"
    bind:value={visibility}
    options={visibilityOptions}
    id="visibility"
    disabled={confidentiality === Confidentiality.CONFIDENTIAL} />
  {#if visibility === Visibility.PUBLIC}
    <div class="formNote" transition:slide|local>
      <Note variant="info">
        <span slot="icon"><InlineSVG src={infoIcon} /></span>
        This project is visible to everyone on the internet!<br />
        Please do not write any private data into this project and blur private data
        in the images if needed.
      </Note>
    </div>
  {/if}
  <Subheading subheading="About" />
  <TextInput
    required
    placeholder={titlePlaceholder}
    label="Title"
    bind:value={title}
    bind:this={titleInputElement}
    id="title" />
  <TextInput
    placeholder={titlePlaceholderEnglish}
    label="Title (EN)"
    bind:value={title_En}
    id="title_En" />
  <TextAreaInput
    required
    placeholder={descriptionPlaceholder}
    language="de"
    onTranslate={handleTranslation}
    bind:value={description}
    label="Description"
    id="description" />
  <TextAreaInput
    placeholder={descriptionPlaceholderEnglish}
    language="en"
    onTranslate={handleTranslation}
    bind:value={descriptionEng}
    label="Description (EN)"
    id="description_En" />
  <DateRangePicker
    type="month"
    required={{start: true, end: false}}
    min={UBILABS_INCORPORATION_DATE}
    label="Creation period"
    bind:value={creationPeriod}
    id="creationPeriod" />
  <Subheading subheading="Project Team" />
  <TagsInput
    placeholder={customersPlaceholder}
    label="Customers"
    bind:value={customers}
    availableTags={$availableCustomers.map(value => ({value}))}
    id="customers" />
  <TagsInput
    required
    placeholder={teamPlaceholder}
    label="Team"
    bind:value={team}
    availableTags={$availableTeamAndProjectLeads.map(value => ({value}))}
    id="team" />
  <TagsInput
    required
    placeholder={projectLeadPlaceholder}
    label="Project Lead"
    bind:value={projectLead}
    availableTags={$availableProjectLeads.map(value => ({value}))}
    id="project_lead" />
  <TextInput
    placeholder={contactNamePlaceholder}
    label="Contact Person"
    bind:value={contactName}
    id="contactName" />
  <TextInput
    class="contactUrl"
    placeholder={contactUrlPlaceholder}
    bind:value={contactUrl}
    id="contactUrl" />
  <Subheading
    subheading={projectType === ProjectType.CONSULTING
      ? 'Project Specs'
      : 'Technical Details'} />
  {#if projectType === ProjectType.CONSULTING}
    <TagsInput
      required
      editable={false}
      placeholder={Object.values(Format).slice(0, 3).join(', ')}
      label="Format"
      tooltipText="What is the format or framework of the project?"
      bind:value={formats}
      availableTags={Object.values(Format).map(value => ({value}))}
      id="format" />
    <TagsInput
      required
      placeholder={methodsPlaceholder}
      label="Methods"
      tooltipText="Which methods were used?"
      bind:value={methods}
      availableTags={[]}
      id="methods" />
  {:else}
    <TagsInput
      required
      placeholder={tasksPlaceholder}
      label="Use cases"
      tooltipText="Central technical function that describes this application. How do we solve a problem?"
      bind:value={tasks}
      availableTags={availableTasks.map(value => ({value}))}
      id="tasks" />
    <TagsInput
      required
      placeholder={technologiesPlaceholder}
      label="Technologies"
      tooltipText="Which technologies are used in the productive operation of the application?"
      bind:value={technologies}
      availableTags={availableTechnologies.map(value => ({value}))}
      id="technologies" />
  {/if}
  <Subheading subheading="Sales Context" />
  <TagsInput
    required
    placeholder={servicesPlaceholder}
    label="Services"
    tooltipText="The central service Ubilabs is offering to the customer."
    bind:value={services}
    availableTags={availableServices.map(value => ({value}))}
    id="services" />
  <TagsInput
    required
    placeholder={fieldsPlaceholder}
    label="Verticals / Industries"
    tooltipText="For which industry / business sector is the application mainly developed?"
    bind:value={fields}
    availableTags={availableFields.map(value => ({value}))}
    id="fields" />
  <TagsInput
    required
    placeholder={topicsPlaceholder}
    label="Focus / Topic"
    tooltipText="Which topic is dealt with here?"
    bind:value={topics}
    availableTags={availableTopics.map(value => ({value}))}
    id="topics" />

  <div class="diamondWrapper">
    <div>
      <label for="diamond">Diamond</label>
      <div class="diamond">
        <Diamond {diamond} />
      </div>
    </div>
    <div>
      {#each Object.values(DiamondSector) as sector}
        <div>
          <RangeInput
            id="diamond"
            bind:value={diamond[sector]}
            min={0}
            max={4}
            tooltipText={diamondTitles[sector]}>
            {sector}
          </RangeInput>
        </div>
      {/each}

      <Note>
        <span slot="icon"><InlineSVG src={helpIcon} /></span>
        Need help with evaluation? Read our
        <a href={diamondHelpUrl} target="_blank">diamond help document</a>.
      </Note>
    </div>
  </div>
  {#if projectType === ProjectType.CONSULTING}
    <NumberInput
      label="Value in Time (Hours)"
      bind:value={valueInTime}
      step={1}
      min={0}
      id="valueInTime" />
    <NumberInput
      label="Value in Price (Euros)"
      bind:value={valueInPrice}
      step={0.01}
      min={0}
      id="valueInPrice" />
    <TextInput label="Link to Zoho" bind:value={zohoUrl} id="zohoUrl" />
  {/if}
  <Subheading subheading="Visual Content" />
  <FilesInput bind:files label="Images" id="imageUpload" />
  <ImageCaptionsInput bind:files bind:captions bind:images />
  <MultipleTextInputs
    placeholder={videosPlaceholder}
    label="Videos"
    id="videos"
    bind:values={videos}
    onValidate={async value => {
      const isDriveVideoUrl = value && driveVideoUrlRegex.test(value);
      const isInvalidVideoUrl =
        value && !youtubeVideoUrlRegex.test(value) && !isDriveVideoUrl;
      if (isInvalidVideoUrl) {
        return 'Please enter a valid YouTube or Google Drive URL';
      } else if (isDriveVideoUrl) {
        try {
          return !(await isGoogleDriveVideoAccessible(value))
            ? 'Google Drive video has only user permissions. Please ensure the video is accessible.'
            : null;
        } catch {
          return 'Google Drive video is not accessible.';
        }
      }
      return null;
    }} />
  <MultipleUrlInputs
    typePlaceholder={urlTypePlaceholder}
    {urlPlaceholder}
    label="URLs"
    bind:urls
    id="urls"
    {projectType} />
  <Subheading subheading="Related Content" />
  <TagsInput
    editable={false}
    label="Related projects"
    bind:value={relatedProjects}
    availableTags={Array.from($projects.entries()).map(([id, project]) => ({
      value: id,
      label: `${project.title}${
        project.customers?.length ? ` | ${project.customers.join(', ')}` : ''
      }`
    }))}
    id="related" />

  <div class="footer">
    <div>
      <Button
        variant={isDraft ? 'secondary' : 'primary'}
        disabled={isSaving}
        isLoading={isSaving}
        type="button"
        on:click={save}>Save {isDraft ? 'as Draft' : 'Project'}</Button>
    </div>
  </div>
</form>
